import { useMemo, useState } from "react";
import { Loader2, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useCreateTemplate,
    useDeleteTemplate,
    useTemplates,
    useUpdateTemplate
} from "@/api/hooks";
import type { Template } from "@/api/mailApi";

export function TemplatesPage() {
    const templates = useTemplates();
    const create = useCreateTemplate();
    const [draft, setDraft] = useState({ name: "", subject: "", bodyHtml: "" });
    const [filter, setFilter] = useState("");

    const all = templates.data ?? [];
    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (q.length === 0) return all;
        return all.filter((t) =>
            t.name.toLowerCase().includes(q) ||
            (t.subject ?? "").toLowerCase().includes(q)
        );
    }, [all, filter]);

    const submit = () => {
        if (!draft.name.trim()) return;
        create.mutate(
            {
                name: draft.name.trim(),
                subject: draft.subject,
                bodyHtml: draft.bodyHtml
            },
            { onSuccess: () => setDraft({ name: "", subject: "", bodyHtml: "" }) }
        );
    };

    return (
        <div className="mx-auto max-w-3xl">
            <header className="mb-4">
                <h2 className="text-xl font-semibold">Templates</h2>
                <p className="text-sm text-muted-foreground">
                    Canned responses you can paste into a compose window.
                </p>
            </header>

            <section className="mb-6 rounded-lg border bg-card p-4 shadow-sm">
                <h3 className="mb-2 text-sm font-semibold">New template</h3>
                <div className="grid gap-2">
                    <Input
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        placeholder="Name (e.g. ‘Scheduling reply’)"
                    />
                    <Input
                        value={draft.subject}
                        onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                        placeholder="Subject (optional)"
                    />
                    <textarea
                        value={draft.bodyHtml}
                        onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
                        rows={4}
                        placeholder="Body (HTML allowed)"
                        className="w-full rounded-md border p-2 text-sm"
                    />
                    <div className="flex justify-end">
                        <Button onClick={submit} disabled={create.isPending || !draft.name.trim()}>
                            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Add
                        </Button>
                    </div>
                </div>
            </section>

            {all.length > 0 ? (
                <div className="relative mb-3 max-w-sm">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <Input
                        type="search"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Filter by name or subject"
                        aria-label="Filter templates"
                        className="h-8 pl-7 text-sm"
                    />
                </div>
            ) : null}

            {templates.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : all.length === 0 ? (
                <p className="text-sm text-muted-foreground">No templates yet.</p>
            ) : visible.length === 0 ? (
                <p className="text-sm text-muted-foreground">No templates match &ldquo;{filter}&rdquo;.</p>
            ) : (
                <ul className="space-y-2">
                    {visible.map((t) => (
                        <TemplateItem key={t.id} template={t} />
                    ))}
                </ul>
            )}
        </div>
    );
}

function TemplateItem({ template }: { template: Template }) {
    const update = useUpdateTemplate();
    const remove = useDeleteTemplate();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        name: template.name,
        subject: template.subject,
        bodyHtml: template.bodyHtml
    });

    if (!editing) {
        return (
            <li className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                        <h3 className="font-medium">{template.name}</h3>
                        {template.subject ? (
                            <p className="text-xs text-muted-foreground">Subject: {template.subject}</p>
                        ) : null}
                        <div
                            className="prose prose-sm mt-2 max-w-none dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: template.bodyHtml }}
                        />
                    </div>
                    <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                            Edit
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${template.name}`}
                            onClick={() => {
                                if (window.confirm(`Delete template "${template.name}"?`)) {
                                    remove.mutate(template.id);
                                }
                            }}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </li>
        );
    }

    return (
        <li className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="grid gap-2">
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
                <textarea
                    value={draft.bodyHtml}
                    onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
                    rows={4}
                    className="w-full rounded-md border p-2 text-sm"
                />
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditing(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() =>
                            update.mutate(
                                { id: template.id, payload: draft },
                                { onSuccess: () => setEditing(false) }
                            )
                        }
                        disabled={update.isPending}
                    >
                        {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Save
                    </Button>
                </div>
            </div>
        </li>
    );
}
